import {
  generateAsymmetricKeyPair,
  RESCUE_RECIPIENT_KEY_STORAGE_KEY,
} from "~~/shared/utils/cryptography.utils";
import { SigningKey } from "ethers";

const RESCUE_KEY_NOT_REGISTERED = "RESCUE_KEY_NOT_REGISTERED";
const RESCUE_REGISTRATION_INTENT_KEY = "gfal-rescue-registration-intent";

export type RescueRegistrationIntent =
  | {
      action: "create-wallet";
      link?: boolean;
    }
  | {
      action: "update-wallet-keys";
      modal: "keys" | "keys-settings";
      type: MFAType;
    };

export class RescueRegistrationRedirectedError extends Error {
  constructor() {
    super("Rescue public-key registration started");
    this.name = "RescueRegistrationRedirectedError";
  }
}

type RescuePublicKeyResponse = {
  key: string;
};

type RescueRegistrationCallbackResult = {
  status: "success" | "error";
  key?: string;
  intent?: RescueRegistrationIntent | null;
  error?: unknown;
};

const getResponseStatus = (error: any): number | undefined =>
  error?.statusCode ?? error?.status ?? error?.response?.status;

const getResponseData = (error: any): any =>
  error?.data ?? error?.response?._data ?? error?.response?.data;

const getResponseErrorCode = (error: any): string | undefined => {
  const data = getResponseData(error);
  return (
    data?.error ??
    data?.code ??
    data?.payload?.code ??
    data?.payload?.error ??
    undefined
  );
};

const isRescueKeyNotRegistered = (error: any): boolean =>
  getResponseStatus(error) === 404 &&
  getResponseErrorCode(error) === RESCUE_KEY_NOT_REGISTERED;

const normalizeRescuePublicKey = (key: unknown): string => {
  if (typeof key !== "string")
    throw new Error("Invalid registered rescue public key");

  try {
    const compressedKey = SigningKey.computePublicKey(key, true).toLowerCase();
    if (!/^0x0[23][0-9a-f]{64}$/.test(compressedKey)) throw new Error();
    return compressedKey;
  } catch {
    throw new Error("Invalid registered rescue public key");
  }
};

const readRegistrationIntent = (): RescueRegistrationIntent | null => {
  if (!import.meta.client) return null;
  const rawIntent = sessionStorage.getItem(RESCUE_REGISTRATION_INTENT_KEY);
  if (!rawIntent) return null;

  try {
    const intent = JSON.parse(rawIntent) as RescueRegistrationIntent;
    if (intent.action === "create-wallet")
      return { action: intent.action, link: Boolean(intent.link) };
    if (
      intent.action === "update-wallet-keys" &&
      (intent.modal === "keys" || intent.modal === "keys-settings")
    )
      return intent;
    return null;
  } catch {
    return null;
  }
};

const clearRegistrationIntent = () => {
  if (import.meta.client)
    sessionStorage.removeItem(RESCUE_REGISTRATION_INTENT_KEY);
};

export const useRescueController = () => {
  const { $serverApi } = useNuxtApp();
  const userStore = useUserStore();
  const route = useRoute();
  const router = useRouter();
  const { rescueUrl } = useRuntimeConfig().public;
  const rescuePublicKeys = useState<Record<string, string>>(
    "registered-rescue-public-keys",
    () => ({})
  );

  const fetchRegisteredRescuePublicKey = async (): Promise<string> => {
    const response = await $serverApi<GFALResponse<RescuePublicKeyResponse>>(
      "/api/users/self/rescue-key",
      {
        headers: constructHeaders(userStore.authToken),
      }
    );

    if (
      (response.statusCode !== undefined && response.statusCode !== 200) ||
      !response.payload?.key
    ) {
      const error = Object.assign(
        new Error(response.strStatusCode || "Registered rescue public key missing"),
        {
          statusCode: response.statusCode,
          data: {
            error: response.payload?.code,
            message: response.message,
          },
        }
      );
      throw error;
    }

    const key = normalizeRescuePublicKey(response.payload.key);
    if (userStore.id) rescuePublicKeys.value[userStore.id] = key;
    return key;
  };

  const saveRegistrationIntent = (intent?: RescueRegistrationIntent) => {
    if (!import.meta.client || !intent) return;
    sessionStorage.setItem(RESCUE_REGISTRATION_INTENT_KEY, JSON.stringify(intent));
  };

  const startPublicKeyRegistration = (intent?: RescueRegistrationIntent) => {
    if (!import.meta.client) throw new Error("Registration requires a browser");
    if (!userStore.authToken) throw new Error("Missing authenticated user token");
    if (!rescueUrl) throw new Error("Rescue service URL is not configured");

    saveRegistrationIntent(intent);

    const callback = new URL(window.location.href);
    callback.searchParams.delete("purpose");
    callback.searchParams.delete("status");

    const registrationUrl = new URL("otp", `${rescueUrl.replace(/\/$/, "")}/`);
    registrationUrl.searchParams.set("purpose", "registration");
    registrationUrl.searchParams.set("callback", callback.toString());
    registrationUrl.hash = `gfalToken=${encodeURIComponent(userStore.authToken)}`;

    window.location.assign(registrationUrl.toString());
    throw new RescueRegistrationRedirectedError();
  };

  const ensureRegisteredRescuePublicKey = async (options?: {
    intent?: RescueRegistrationIntent;
  }): Promise<string> => {
    const cachedKey = userStore.id ? rescuePublicKeys.value[userStore.id] : null;
    if (cachedKey) return cachedKey;

    try {
      return await fetchRegisteredRescuePublicKey();
    } catch (error) {
      if (!isRescueKeyNotRegistered(error)) throw error;
      startPublicKeyRegistration(options?.intent);
      throw new RescueRegistrationRedirectedError();
    }
  };

  const removeRegistrationCallbackQuery = async () => {
    const query = { ...route.query };
    delete query.purpose;
    delete query.status;
    await router.replace({ path: route.path, query });
  };

  const handlePublicKeyRegistrationCallback = async (): Promise<RescueRegistrationCallbackResult | null> => {
    if (!import.meta.client || route.query.purpose !== "registration")
      return null;

    const intent = readRegistrationIntent();
    const status = route.query.status;
    try {
      if (status !== "success")
        return { status: "error", intent, error: new Error("Rescue registration failed") };

      const key = await fetchRegisteredRescuePublicKey();
      return { status: "success", key, intent };
    } catch (error) {
      return { status: "error", intent, error };
    } finally {
      clearRegistrationIntent();
      await removeRegistrationCallbackQuery();
    }
  };

  const clearStalePublicKeyRegistrationIntent = () => {
    if (import.meta.client && route.query.purpose !== "registration")
      clearRegistrationIntent();
  };

  const createOTPAuthAttemptAndRedirect = async () => {
    const startTime = Date.now();
    const recipientKeyPair = generateAsymmetricKeyPair();
    const requestBody = {
      email: useUserStore().email,
      recipientPublicKey: recipientKeyPair.publicKey,
    };

    try {
      sessionStorage.setItem(
        RESCUE_RECIPIENT_KEY_STORAGE_KEY,
        JSON.stringify(recipientKeyPair)
      );

      const requestOtpAuth = async () => {
        try {
          return await $serverApi<GFALResponse<string | null>>(
            "/oauth/rescue/otp",
            {
              method: "POST",
              headers: constructHeaders(useUserStore().authToken),
              body: requestBody,
            },
          );
        } catch {
          return await $fetch<GFALResponse<string | null>>("/oauth/rescue/otp", {
            method: "POST",
            headers: constructHeaders(useUserStore().authToken),
            body: requestBody,
          });
        }
      };
      const [response] = await Promise.all([
        requestOtpAuth(),
        new Promise((resolve) => {
          const elapsed = Date.now() - startTime;
          const remainingTime = Math.max(1000 - elapsed, 0);
          setTimeout(resolve, remainingTime);
        }),
      ]);

      if (response.statusCode === 200 && response.payload) {
        navigateTo(response.payload, { external: true });
      } else {
        throw new Error("Failed to initialize OTP login");
      }
    } catch (error) {
      sessionStorage.removeItem(RESCUE_RECIPIENT_KEY_STORAGE_KEY);
      console.error("Failed to initialize OTP login", error);
      throw error;
    }
  };

  return {
    createOTPAuthAttemptAndRedirect,
    ensureRegisteredRescuePublicKey,
    fetchRegisteredRescuePublicKey,
    handlePublicKeyRegistrationCallback,
    clearStalePublicKeyRegistrationIntent,
    startPublicKeyRegistration,
  };
};
